"use client";
import { Avatar, Box, Button, Divider, Grid2, Typography } from "@repo/ui";
import Section from "../components/Section";
import Image from "next/image";
import MacWindow from "../components/MacWindow";
import { UsersOutline } from "@spoctech/icons";
import React from "react";
import { hasData } from "@/lib/helpers";

type BecomeAnInstructorProps = {
  sectionInfo: PageSections;
  data: {
    heading: string;
    description: string;
    buttonText: string;
    buttonLink: string;
    images: {
      imageAltText: string;
      imageUrl: string;
    }[];
  };
};

const BecomeAnInstructor = ({ data, sectionInfo }: BecomeAnInstructorProps) => {
  if (!hasData(data)) return null;

  return (
    <Section
      id={sectionInfo.sectionId}
      className={sectionInfo.sectionClass}
      innerBoxProps={{ className: "bg-[#E5E7FD] rounded-lg sm:px-16 px-4" }}
    >
      <Grid2 container spacing={3} align="center" className="m-0">
        <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6 }}>
          <Box>
            <Typography variant="h2" gutterBottom>
              {data.heading}
            </Typography>
            <Typography paragraph>{data.description}</Typography>
          </Box>
          <Box className="mt-8">
            <Button
              color="primary"
              variant="outlined"
              href={data.buttonLink || "/"}
              label={data.buttonText || "Start teaching today"}
            />
          </Box>
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6 }}>
          <Box className="overflow-hidden relative w-full block h-full rounded-lg">
            <Image
              src="/images/become-an-instructor-bg.png"
              alt="bg"
              fill
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
              className="object-contain pr-8 pb-8"
            />
            <Box className="pl-16 pt-16">
              <MacWindow className="col-span-10 row-span-full row-start-2 col-end-12 relative z-10">
                <Box flex className="relative">
                  <Avatar className="absolute left-[-36px] !bg-white ring-[10px] rotate-[345deg]">
                    <UsersOutline className="text-primary-main" />
                  </Avatar>
                  {data.images?.map((image, index) => (
                    <React.Fragment key={`instructor-${index}`}>
                      <Box p={"lg"}>
                        <Image
                          src={image.imageUrl}
                          width={280}
                          height={280}
                          alt={image.imageAltText || ""}
                          className="rounded-xl"
                        />
                      </Box>
                      {index < data.images.length - 1 && (
                        <Divider orientation="vertical" />
                      )}
                    </React.Fragment>
                  ))}
                </Box>
              </MacWindow>
            </Box>
          </Box>
        </Grid2>
      </Grid2>
    </Section>
  );
};

export default BecomeAnInstructor;
